import {
  Issue,
  IssueCustomAttributeDefinitionCommon,
} from "@adsk/acc-issues-types";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  getCustomFieldEntitiesMap,
  getOrderedCustomFields,
} from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import { getMappedCustomAttributeIdSet } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFieldMappings";
import {
  customAttributeCellRenderer,
  renderHeaderWithTooltip,
  CheckboxHeaderCell,
  ActionCell,
} from "./cellRenderers";
import { ISSUE_COLUMN_KEYS, ISSUE_COLUMN_SORT_ATTRS } from "./columnKeys";
import { useContext, useMemo } from "react";
import {
  useFeatureFlag,
  FeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { useLocalizedDecimalSeparator } from "@adsk/acc-issues-core/utils/hooks/useLocalizedDecimalSeparator";
import { createColumnHelper, Table, ColumnDef } from "@tanstack/react-table";
import { useTableColumns } from "./useTableColumns";

export const useColumnKeysInDefaultOrder = (): ISSUE_COLUMN_KEYS[] => {
  const { value: showReferenceCountInIssuesTable } = useFeatureFlag(
    FeatureFlag.ShowReferenceCountInIssueTable
  );
  const { value: showAttachmentsCountInIssuesTable } = useFeatureFlag(
    FeatureFlag.Attachment1_1IssuesTableColumn
  );
  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);
  return [
    ISSUE_COLUMN_KEYS.ID,
    ISSUE_COLUMN_KEYS.TITLE,
    ISSUE_COLUMN_KEYS.STATUS,
    ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY,
    ISSUE_COLUMN_KEYS.ISSUE_TYPE,
    ISSUE_COLUMN_KEYS.DESCRIPTION,
    ISSUE_COLUMN_KEYS.ASSIGNED_TO,
    isDeletedSpaceActive && ISSUE_COLUMN_KEYS.DELETED_BY,
    isDeletedSpaceActive && ISSUE_COLUMN_KEYS.DELETED_ON,
    ISSUE_COLUMN_KEYS.WATCHERS,
    ISSUE_COLUMN_KEYS.COMPANY,
    ISSUE_COLUMN_KEYS.LBS_LOCATION,
    ISSUE_COLUMN_KEYS.LOCATION_DETAILS,
    ISSUE_COLUMN_KEYS.DUE_DATE,
    ISSUE_COLUMN_KEYS.START_DATE,
    ISSUE_COLUMN_KEYS.PLACEMENT,
    ISSUE_COLUMN_KEYS.ROOT_CAUSE,
    ISSUE_COLUMN_KEYS.IS_PUBLISHED,
    ISSUE_COLUMN_KEYS.CREATED_BY,
    ISSUE_COLUMN_KEYS.CREATED_AT,
    ISSUE_COLUMN_KEYS.UPDATED_BY,
    ISSUE_COLUMN_KEYS.UPDATED_AT,
    showAttachmentsCountInIssuesTable && ISSUE_COLUMN_KEYS.ATTACHMENTS,
    showReferenceCountInIssuesTable && ISSUE_COLUMN_KEYS.REFERENCES,
  ].filter(Boolean);
};

export const useGetInitialColumnVisibility = (): Record<string, boolean> => {
  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);
  const { value: showReferenceCountInIssuesTable } = useFeatureFlag(
    FeatureFlag.ShowReferenceCountInIssueTable
  );

  const { value: showAttachmentsCountInIssuesTable } = useFeatureFlag(
    FeatureFlag.Attachment1_1IssuesTableColumn
  );
  const mappedCustomAttributeIdSet = useSelector(getMappedCustomAttributeIdSet);
  const customFields = useSelector(getOrderedCustomFields);

  return useMemo(() => {
    const mappedCustomFieldsAsHidden = customFields
      .filter((customField: IssueCustomAttributeDefinitionCommon) =>
        mappedCustomAttributeIdSet.has(customField.id)
      )
      .reduce(
        (
          acc: Record<string, boolean>,
          customField: IssueCustomAttributeDefinitionCommon
        ) => {
          acc[`custom_${customField.id}`] = false;
          return acc;
        },
        {}
      );

    const hiddenColumns: Record<string, boolean> = {
      [ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY]: false,
      [ISSUE_COLUMN_KEYS.DESCRIPTION]: false,
      [ISSUE_COLUMN_KEYS.LBS_LOCATION]: false,
      [ISSUE_COLUMN_KEYS.LOCATION_DETAILS]: false,
      [ISSUE_COLUMN_KEYS.WATCHERS]: false,
      [ISSUE_COLUMN_KEYS.COMPANY]: false,
      [ISSUE_COLUMN_KEYS.ROOT_CAUSE]: false,
      [ISSUE_COLUMN_KEYS.IS_PUBLISHED]: false,
      [ISSUE_COLUMN_KEYS.CREATED_BY]: false,
      [ISSUE_COLUMN_KEYS.CREATED_AT]: false,
      [ISSUE_COLUMN_KEYS.UPDATED_BY]: false,
      [ISSUE_COLUMN_KEYS.UPDATED_AT]: false,
      ...mappedCustomFieldsAsHidden,
    };

    if (showAttachmentsCountInIssuesTable) {
      hiddenColumns[ISSUE_COLUMN_KEYS.ATTACHMENTS] = false;
    }
    if (showReferenceCountInIssuesTable) {
      hiddenColumns[ISSUE_COLUMN_KEYS.REFERENCES] = false;
    }

    if (isDeletedSpaceActive) {
      hiddenColumns[ISSUE_COLUMN_KEYS.DELETED_BY] = true;
      hiddenColumns[ISSUE_COLUMN_KEYS.DELETED_ON] = true;
    }

    return hiddenColumns;
  }, [
    isDeletedSpaceActive,
    showReferenceCountInIssuesTable,
    showAttachmentsCountInIssuesTable,
    customFields,
    mappedCustomAttributeIdSet,
  ]);
};

const getContainerFromTable = (table: Table<Issue>) => {
  const meta = table.options.meta as any;
  return meta?.container?.current || null;
};

export const useTableColumnDefs = () => {
  const customFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);
  const columnHelper = useMemo(() => createColumnHelper<Issue>(), []);

  const allTableColumns = useTableColumns();
  const [baseSelectColumn, ...standardColumnDefs] = allTableColumns;

  const checkboxColumn = useMemo<ColumnDef<Issue, unknown>>(
    () => ({
      ...baseSelectColumn,
      header: CheckboxHeaderCell,
    }),
    [baseSelectColumn]
  );

  const standardColumns = useMemo(
    () =>
      standardColumnDefs.map(colDef => {
        return columnHelper.accessor(colDef.accessorKey as keyof Issue, {
          id: colDef.id as string,
          header: (info: { table: Table<Issue> }) => {
            const container = getContainerFromTable(info.table);
            return renderHeaderWithTooltip(colDef.header as string, container);
          },
          size: colDef.size,
          minSize: colDef.minSize,
          enableSorting:
            !!ISSUE_COLUMN_SORT_ATTRS[colDef.id as ISSUE_COLUMN_KEYS],
          sortDescFirst: colDef.sortDescFirst,
          enableResizing: colDef.enableResizing,
          enableHiding:
            colDef.id !== ISSUE_COLUMN_KEYS.ID &&
            colDef.id !== ISSUE_COLUMN_KEYS.STATUS,
          cell: colDef.cell,
          meta: {
            type: "standard" as const,
            headerText: colDef.header as string,
          },
        });
      }) as ColumnDef<Issue, unknown>[],
    [standardColumnDefs, columnHelper]
  );

  const { formatLocalizedNumber } = useLocalizedDecimalSeparator();
  const { value: shouldSortCustomFields } = useFeatureFlag(
    FeatureFlag.CustomFieldSorting
  );

  const mappedCustomAttributeIdSet = useSelector(getMappedCustomAttributeIdSet);
  const customFieldKeys: string[] = useSelector(getOrderedCustomFields)
    .map((customField: IssueCustomAttributeDefinitionCommon) => customField.id)
    .filter((id: string) => mappedCustomAttributeIdSet.has(id));

  const customColumns = useMemo(
    () =>
      customFieldKeys.map(key =>
        columnHelper.accessor(
          (row: Issue) =>
            row.customAttributes?.find(
              (attr: { attributeDefinitionId: string }) =>
                attr.attributeDefinitionId === key
            ) as unknown,
          {
            id: `custom_${key}`,
            header: (info: { table: Table<Issue> }) => {
              const container = getContainerFromTable(info.table);
              return renderHeaderWithTooltip(
                customFieldEntitiesMap[key].title,
                container
              );
            },
            size: 120,
            minSize: 120,
            enableSorting: shouldSortCustomFields,
            enableHiding: true,
            cell: info =>
              customAttributeCellRenderer({
                customAttribute: info.getValue(),
                customField: customFieldEntitiesMap[key],
                formatLocalizedNumber,
              }),
            meta: {
              type: "custom" as const,
              headerText: customFieldEntitiesMap[key].title,
            },
          }
        )
      ) as ColumnDef<Issue, unknown>[],
    [
      customFieldKeys,
      columnHelper,
      customFieldEntitiesMap,
      formatLocalizedNumber,
      shouldSortCustomFields,
    ]
  );

  const ACTIONS_COLUMN_SIZE = 52;

  const actionsColumn = useMemo(
    () =>
      columnHelper.display({
        id: "actions",
        size: ACTIONS_COLUMN_SIZE,
        minSize: ACTIONS_COLUMN_SIZE,
        maxSize: ACTIONS_COLUMN_SIZE,
        enableResizing: false,
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        cell: ActionCell,
        meta: {
          sticky: "right",
        },
      }),
    [columnHelper]
  );

  return useMemo(
    () =>
      [
        checkboxColumn,
        ...standardColumns,
        ...customColumns,
        actionsColumn,
      ] as ColumnDef<Issue, unknown>[],
    [checkboxColumn, standardColumns, customColumns, actionsColumn]
  );
};
